import { z } from "zod";
import type { AuditResult } from "./records.js";
/**
 * Who may do what, and where (requirements.md section 10.7, ADR-0008).
 * A role is held per environment, never across the organization, and
 * changing one is manager-only.
 */

export interface MembershipView {
  userId: string;
  /** ADR-0017: every role held here - built-in ids or the organization's own. */
  roleIds: string[];
  /** ADR-0017 section 2b: which profiles the person may see on this environment. */
  allProfiles: boolean;
  profileIds?: string[] | undefined;
  /** Roles that reach this person through a group on this environment, by group. */
  viaGroups?: Array<{ groupId: string; name: string; roleIds: string[] }> | undefined;
  grantedBy: string;
  grantedAt: string;
  email?: string | undefined;
  lastSeenAt?: string | undefined;
}

export interface MembershipsResponse {
  environmentId: string;
  memberships: MembershipView[];
  /** The roles the rows name, by id - so the screen can name them without the organization's roles list, which Gairm staff outside view-as cannot read (ADR-0013). */
  roleNames: Record<string, string>;
  /** Anyone in the organization who has signed in and holds no role here yet. */
  candidates: Array<{ userId: string; email?: string | undefined }>;
  /** An environment with no manager cannot have its roles changed back. */
  managerCount: number;
}

export interface ChainEnvironment {
  environmentId: string;
  friendlyName: string;
  /** Declared, never inferred from position in the chain (ADR-0008). */
  live: boolean;
  changesRequireApproval: boolean;
  breakGlassEnabled: boolean;
}

export interface ChainResponse {
  organisationId: string;
  name: string;
  chain: ChainEnvironment[];
}

export interface EnvironmentSettingsInput {
  live?: boolean;
  changesRequireApproval?: boolean;
  breakGlassEnabled?: boolean;
}

/** POST .../memberships: the roles now held (ADR-0017). */
export interface GrantRoleInput {
  userId: string;
  roleIds: string[];
  /** Absent keeps what the membership has (all profiles for a new one). */
  allProfiles?: boolean;
  profileIds?: string[];
}
export interface GrantRoleResponse {
  ok: true;
  roleIds: string[];
  allProfiles: boolean;
  profileIds?: string[] | undefined;
}

// ---- ADR-0017: roles, groups, effective access, staff ----

export interface RoleView {
  roleId: string;
  name: string;
  description?: string | undefined;
  permissions: string[];
  builtIn: boolean;
  /** How many memberships and groups name it - shown before a delete. */
  heldBy: number;
  updatedAt: string;
}
export interface RolesResponse {
  roles: RoleView[];
  /** Whether the caller may create, edit and delete roles here. */
  canManage: boolean;
}
export interface RoleInput {
  name: string;
  description?: string;
  permissions: string[];
}
export interface RoleCreatedResponse {
  ok: true;
  roleId: string;
}

export interface GroupView {
  groupId: string;
  name: string;
  roleIds: string[];
  members: Array<{ userId: string; email?: string | undefined }>;
  environmentIds: string[];
  updatedAt: string;
}
export interface GroupsResponse {
  groups: GroupView[];
  canManage: boolean;
}
export interface GroupInput {
  name: string;
  roleIds: string[];
  memberUserIds: string[];
  environmentIds: string[];
}
export interface GroupCreatedResponse {
  ok: true;
  groupId: string;
}

/** Platform › Staff (superadmin only; a reader sees it and cannot change it). */
export interface StaffResponse {
  staff: Array<{ userId: string; email?: string | undefined; displayName?: string | undefined; staffRole: "gairm-staff" | "gairm-reader" | "gairm-superadmin"; lastSeenAt?: string | undefined }>;
  /** environmentId → excluded staff subjects. */
  exclusions: Record<string, string[]>;
  canManage: boolean;
}
export interface StaffRoleInput {
  userId: string;
  staffRole: "gairm-staff" | "gairm-reader" | "gairm-superadmin";
}
export interface StaffExclusionInput {
  environmentId: string;
  userId: string;
  excluded: boolean;
}

/** PUT .../chain: the order as now stored. */
export interface ReorderChainResponse {
  ok: true;
  chain: string[];
}

/** PUT .../settings: which fields moved - each is audited separately - or that none did. */
export type EnvironmentSettingsResponse = { ok: true; unchanged: true } | { ok: true; changed: string[] };

/** POST /v1/organisations/me/users: the invited user, as Cognito created them (ADR-0013). */
export interface InviteUserResponse {
  userId: string;
  email: string;
}

/** The audit trail as the portal shows it (requirements.md section 11). */
export interface AuditEntryView {
  id: string;
  timestamp: string;
  action: string;
  environmentId?: string;
  actor: string;
  /** Section 13.2: the display name at the time, shown as "Name (email)"; the email stays the actor of record. */
  actorName?: string;
  /** The first question asked after an incident; never left to inference. */
  actorIsInternal: boolean;
  /** Gairm staff, acting through the customer's portal view (ADR-0011). */
  viewAs?: boolean;
  authority: "not_required" | "approved" | "self_approved" | "break_glass";
  approvedBy?: string;
  result: AuditResult;
  reversible: boolean;
  changes: Array<{ field: string; before?: unknown; after?: unknown; kept_target_value?: boolean | undefined }>;
  rolledBackFrom?: string;
  requestId?: string;
  details?: Record<string, unknown>;
}

export interface AuditResponse {
  environmentId: string;
  scope: "environment" | "organisation";
  entries: AuditEntryView[];
  nextCursor?: string | undefined;
}

/** The audit rows a person's reads of one contact left behind (ADR-0015 section 9). */
export interface ContactAuditResponse {
  contactId: string;
  entries: AuditEntryView[];
  /** The query stopped at its page limit before reaching the oldest rows. */
  truncated: boolean;
}

/** ADR-0013: the organization's people, as a manager sees them. */
export interface OrganisationUsersResponse {
  organisationId: string;
  /** Whether the caller may invite, disable and remove - a manager on at least one environment. */
  canManage: boolean;
  users: Array<{
    userId: string;
    email?: string;
    displayName?: string;
    /** Never signed in: an invitation not yet taken up. */
    invitedAt?: string;
    invitedBy?: string;
    lastSeenAt?: string;
    disabledAt?: string;
    removedAt?: string;
    /** #591: the person asked for help signing in and proved their mailbox; a manager can reset or resend. */
    helpRequest?: { kind: SignInHelpKind; at: string; reference: string };
    /** Environments where the user holds a role, with every role the membership names (ADR-0017). */
    roles: Array<{ environmentId: string; roleIds: string[] }>;
    /** ADR-0017: the groups the person is in. */
    groupIds?: string[] | undefined;
  }>;
}

/** #591: what a person who cannot sign in asks for. */
export const SignInHelpKindSchema = z.enum(["authenticator", "invitation"]);
export type SignInHelpKind = z.infer<typeof SignInHelpKindSchema>;
/** POST /v1/public/sign-in-help: the address and what is wrong. The answer never says whether an account exists. */
export const SignInHelpRequestSchema = z.object({ email: z.string().trim().toLowerCase().max(254).email(), kind: SignInHelpKindSchema });
/** POST /v1/public/sign-in-help/confirm: the token from the link mailed to that address. */
export const SignInHelpConfirmSchema = z.object({ token: z.string().min(20).max(400) });
export interface SignInHelpConfirmedResponse {
  reference: string;
  kind: SignInHelpKind;
}
